<template>
    <div class="from-base-200/70 to-base-100 relative flex size-full grow flex-col flex-wrap bg-gradient-to-t
                sm:flex-row">
        <div class="flex w-full flex-wrap items-center justify-evenly gap-x-6 gap-y-2 px-4 py-6
                    sm:hidden sm:justify-between">
            <template v-if="Object.keys(filters).length">
                <div class="indicator xs:w-auto w-full">
                    <span v-show="hasFilterSelected" class="badge indicator-item badge-accent badge-xs xs:badge-sm" />
                    <button class="btn btn-sm xs:btn-md w-full font-light capitalize"
                            @click="$refs.categories.showModal()">
                        Categories
                    </button>
                </div>

                <dialog ref="categories" class="modal">
                    <div class="modal-box">
                        <h3 class="text-lg font-bold">
                            Categories
                        </h3>

                        <div class="mt-6 grid w-full grid-flow-col grid-rows-4 gap-3 sm:block">
                            <div v-for="name in Object.keys(filters) as ProductType[]" :key="name">
                                <label class="label cursor-pointer justify-start">
                                    <input :checked="filters[name]"
                                           type="checkbox"
                                           class="checkbox-primary checkbox mr-3"
                                           @change="updateFilters(name)">
                                    <span>
                                        {{ displayName(name) }}
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div class="modal-action">
                            <form method="dialog">
                                <!-- if there is a button in form, it will close the modal -->
                                <button class="btn">
                                    Close
                                </button>
                            </form>
                        </div>
                    </div>

                    <form method="dialog" class="modal-backdrop">
                        <button>close</button>
                    </form>
                </dialog>
            </template>

            <ClientOnly>
                <TransitionExpand appear>
                    <label v-if="data.length
                               && favourites.length
                               && favourites.some(id => products.map(p => p.id).includes(id))"
                           class="label cursor-pointer justify-start sm:hidden">
                        <input v-model="favouritesOnly"
                               type="checkbox"
                               class="checkbox-primary checkbox mr-3">
                        <span class="text-sm sm:text-lg">Favourites only</span>
                    </label>
                </TransitionExpand>
            </ClientOnly>

            <label v-if="data.length" class="label cursor-pointer justify-start">
                <input :checked="latest"
                       type="checkbox"
                       class="checkbox-primary checkbox mr-3"
                       @change="latest = !latest">
                <span>
                    Order by latest
                </span>
            </label>
        </div>
        <aside v-if="Object.keys(filters).length && data.length"
               class="top-0 hidden w-full flex-col self-start px-4 py-6
                      sm:sticky sm:flex sm:w-2/5 md:w-1/4 lg:w-1/5 2xl:w-1/6">
            <h3 class="text-base-content">
                Categories:
            </h3>
            <div class="mt-6 grid w-full grid-flow-col grid-rows-4 gap-3 sm:block">
                <div v-for="name in Object.keys(filters) as ProductType[]" :key="name">
                    <label class="label cursor-pointer justify-start">
                        <input :checked="filters[name]"
                               type="checkbox"
                               class="checkbox-primary checkbox mr-3"
                               @change="updateFilters(name)">
                        <span>
                            {{ displayName(name) }}
                        </span>
                    </label>
                </div>
            </div>

            <ClientOnly>
                <TransitionExpand appear>
                    <label v-if="favourites.length && favourites.some(id => products.map(p => p.id).includes(id))"
                           class="label mt-6 hidden cursor-pointer justify-start sm:flex">
                        <input v-model="favouritesOnly"
                               type="checkbox"
                               class="checkbox-primary checkbox mr-3">
                        <span class="text-sm sm:text-lg">Favourites only</span>
                    </label>
                </TransitionExpand>
            </ClientOnly>

            <div class="mt-6">
                <label class="label cursor-pointer justify-start">
                    <input :checked="latest"
                           type="checkbox"
                           class="checkbox-primary checkbox mr-3"
                           @change="latest = !latest">
                    <span>
                        Order by latest
                    </span>
                </label>
            </div>
        </aside>
        <div class="border-primary/50 container my-6 w-full sm:w-3/5 md:w-3/4 lg:w-4/5 2xl:w-5/6"
             :class="{ 'sm:border-l-2': Object.keys(filters).length && data.length }"
             aria-live="polite">
            <template v-if="products.length">
                <TransitionScale group
                                 tag="div"
                                 class="grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2
                                        lg:grid-cols-3 2xl:grid-cols-4">
                    <PublicProductTile v-for="(product, i) in visibleProducts"
                                       :key="product.id"
                                       :product="product"
                                       :eager="i < 2"
                                       class="mx-auto" />
                    <NuxtLink v-if="visibleProducts.length === products.length"
                              key="contact"
                              :to="{ name: 'contact' }"
                              class="card relative mx-auto w-full max-w-[300px] rounded-lg bg-white/50 text-clip
                                     no-underline shadow">
                        <figure class="w-full">
                            <figcaption style="height: 250px"
                                        class="bg-base-300/70 text-base-content/40 flex w-full flex-col items-center justify-center
                                           space-y-2">
                                <Logo class="size-10" />
                                <p class="px-4 text-center">
                                    Haven't found what you're looking for?
                                </p>
                            </figcaption>
                        </figure>
                        <div class="card-body">
                            <h2 class="card-title 2xs:text-base flex-wrap justify-center text-sm sm:justify-start
                                   sm:text-lg">
                                Contact me about a custom order!
                            </h2>
                        </div>
                    </NuxtLink>
                </TransitionScale>
                <div v-if="visibleProducts.length < products.length"
                     ref="loadMore"
                     aria-hidden="true" />
            </template>
            <div v-else>
                <div class="flex flex-col items-center justify-center space-y-8">
                    <Icon name="mdi:cupcake" class="text-base-content/50" />
                    <span v-if="status === 'pending'"
                          class="loading loading-ring loading-lg text-primary scale-150" />
                    <p v-else class="text-base-content/40 text-center text-2xl font-bold select-none">
                        <template v-if="publishedProducts.length > products.length">
                            Unfortunately, we don't have any products that match your filters.
                            <br>
                            Check back later!
                        </template>
                        <template v-else>
                            Unfortunately we don't have any products yet.
                            <br>
                            Check back later!
                        </template>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ProductType } from '~~/app/composables/useProductTypes';
import type { ProductAttributes } from '~/types';

defineOptions({
    // eslint-disable-next-line vue/component-definition-name-casing,vue/match-component-file-name
    name: 'products'
});

const displayName = (productType: ProductType) => {
    if (productType === 'pie-tart') {
        return 'Pies and Tarts';
    }

    return productType.charAt(0).toUpperCase() + productType.slice(1) + 's';
};

useSchemaOrg([
    defineWebPage({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '@type': 'CollectionPage'
    })
]);

const api = useApi();
const { data, status, error } = await useAsyncData(
    'products',
    async () => getAllProducts(api),
    {
        default: () => [] as ProductAttributes[],
        getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]
    }
);

// a failed fetch still rendered a 200 with no products, which the swr route rule then cached for an hour;
// 503 isn't cached and tells crawlers to come back; the browser fetches the products itself
if (import.meta.server && error.value) {
    setResponseStatus(useRequestEvent()!, 503);
}
const router = useRouter();
const publishedProducts = computed<ProductAttributes[]>(
    // if it doesn't have the inDraft property, then it's a product shape for a visitor,
    // or if it does, and it's false, then filter that too
    () => data.value.filter(product => !('inDraft' in product) || product.inDraft === false)
);
const favouritesOnly = ref(false);
const { favourites } = useProductFavourites();
/**
 * Currently unused...
 */
const search = ref('');
const latest = ref(false);
const products = computed(() => {
    let products = [...publishedProducts.value];

    if (Object.values(filters.value).some(filter => filter)) {
        products = products.filter(product => filters.value[product.type]);
    }

    if (favouritesOnly.value) {
        products = products.filter(product => favourites.value.includes(product.id));
    }

    if (latest.value) {
        products.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    return products;
});
const getFiltersFor = (products: ProductAttributes[]): Partial<Record<ProductType, boolean>> => {
    // get unique product types
    // @ts-expect-error - idk
    return [...new Set(products.map(product => product.type))]
        // create a filter [name: boolean] map
        .reduce<Record<ProductType, boolean>>((acc, type) => {
            acc[type] = false;
            return acc;
            // @ts-expect-error - idk
        }, {});
};
const filters = useState<ReturnType<typeof getFiltersFor>>(() => ({}));
const hasFilterSelected = computed(() => Object.values(filters.value).some(filter => filter));
const toggleFilter = (type: ProductType) => {
    (Object.keys(filters.value) as ProductType[])
        // toggle the filter for the type
        .forEach(key => filters.value[key] = key === type && !filters.value[key]);
};
// all 80 tiles made hydration (and Sentry Replay's DOM snapshot) the biggest main-thread cost on this page,
// so render a page's worth and add more as the user nears the end of the grid
const tilesPerBatch = 12;
const visibleCount = ref(tilesPerBatch);
const visibleProducts = computed(() => products.value.slice(0, visibleCount.value));
const loadMore = useTemplateRef('loadMore');
useIntersectionObserver(
    loadMore,
    ([entry]) => {
        if (entry?.isIntersecting) {
            visibleCount.value += tilesPerBatch;
        }
    },
    { rootMargin: '800px' }
);
watch(products, () => visibleCount.value = tilesPerBatch);
watch(
    () => publishedProducts.value,
    newPublishedProducts => {
        filters.value = getFiltersFor(newPublishedProducts);
    }
);
watchDebounced(
    () => search.value,
    newSearch => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { search, ...query } = router.currentRoute.value.query;

        if (newSearch) {
            query.search = newSearch;
        }

        void router.replace(router.resolve({ ...router.currentRoute.value, query }));
    },
    { debounce: 500 }
);
const activeType = computed(() => {
    const type = router.currentRoute.value.query.type;
    return typeof type === 'string' ? type as ProductType : null;
});
const pageTitle = computed(() => {
    if (activeType.value) {
        return `${displayName(activeType.value)} | Products | Cina's Delicious Cakes`;
    }
    return 'Products | Cina\'s Delicious Cakes';
});
const description = computed(() => {
    if (activeType.value) {
        return `Browse our ${displayName(activeType.value).toLowerCase()} made by Cina's Delicious Cakes.`;
    }
    return 'Products made by Cina\'s Delicious Cakes.';
});

useSeoMeta({
    title: pageTitle,
    description,
    ogTitle: pageTitle,
    ogDescription: description,
    twitterTitle: pageTitle,
    twitterDescription: description
});

filters.value = getFiltersFor(publishedProducts.value);

// update filters from query

if (typeof router.currentRoute.value.query.type === 'string'

    && router.currentRoute.value.query.type in filters.value
) {
    toggleFilter(router.currentRoute.value.query.type as ProductType);
}

if (typeof router.currentRoute.value.query.search === 'string') {
    search.value = decodeURIComponent(router.currentRoute.value.query.search);
}

const updateFilters = async (type: ProductType) => {
    toggleFilter(type);

    const filteredProducts = publishedProducts.value
        .filter(product => filters.value[product.type])
        .map(p => p.id);

    // if none are favourite, remove favouritesOnly filter
    if (!filteredProducts.some(id => favourites.value.includes(id))) {
        favouritesOnly.value = false;
    }

    const query = { ...router.currentRoute.value.query };

    if (query.type === type) {
        delete query.type;
    } else {
        query.type = type;
    }

    // resolve so the full path and href gets updated with the new query
    await router.replace(router.resolve({ ...router.currentRoute.value, query }));
};
</script>
